/** @jsxImportSource @emotion/react */
import { useSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { PageLoader } from 'components/PageLoader'
import TabsNavigation from 'components/TabsNavigation'
import { DescriptionForm } from 'components/DescriptionForm'
import { FIND_ONE_SUBJECT } from 'queries/subject'
import { findOneSubject, findOneSubjectVariables } from '__generatedTypes__/findOneSubject'
import { TabsItem } from 'types/tabsItem'
import {
  CASE_BASED_SCENARIOS_TAB_INDEX,
  CASE_SCENARIO_SUBJECT_IDS,
  getCBSDescriptionPage,
  getCBSFaqPage,
  getQBTitle,
} from '../QuestionBanks/utils'
import { ListWithoutCategories } from '../QuestionBanks/List/ListWithoutCategories'
import FAQ from '../FAQ'
import * as classes from './styles'

export const QUERY_PARAM = 'tab'

const QUESTIONS_TAB = 'Questions'
const DESCRIPTION_TAB = 'Description'
const FAQ_TAB = 'FAQ'

const TABS: Array<{ id: string; title: string }> = [
  {
    id: QUESTIONS_TAB,
    title: 'Questions',
  },
  {
    id: DESCRIPTION_TAB,
    title: 'Description',
  },
  {
    id: FAQ_TAB,
    title: 'FAQ',
  },
]

export type CaseBasedScenarioProps = {
  tabIndex?: number
}

export const CaseBasedScenario = ({ tabIndex = CASE_BASED_SCENARIOS_TAB_INDEX }: CaseBasedScenarioProps) => {
  const { navigate } = useLink()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedPlatform, selectedPlatformSlug } = usePlatformsAndSubjects({})
  const caseScenarioSubjectId = CASE_SCENARIO_SUBJECT_IDS[selectedPlatformSlug] ?? null
  const subjectState = useQuery<findOneSubject, findOneSubjectVariables>(FIND_ONE_SUBJECT, {
    variables: {
      id: String(caseScenarioSubjectId),
    },
    skip: !caseScenarioSubjectId,
  })
  const subject = subjectState.data?.findOneSubject

  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(QUERY_PARAM)

    return TABS.find(({ id }) => id === tabId) || TABS[0]
  })

  const handleChangeTab = (activeTab: TabsItem) => {
    setSearchParams((prev) => ({
      ...Object.fromEntries(prev.entries()),
      [QUERY_PARAM]: activeTab.id,
    }))
    setActiveTab(activeTab)
  }

  useEffect(() => {
    if (!caseScenarioSubjectId) {
      navigate(`/${String(selectedPlatformSlug)}/${cmsRoutes.NOT_FOUND}`)
    }
  }, [caseScenarioSubjectId])

  const renderTab = () => {
    if (!subject) {
      return <></>
    }

    switch (activeTab.id) {
      case QUESTIONS_TAB:
        return (
          <ListWithoutCategories
            selectedSubject={{
              ...subject,
              tabId: subject.id,
              tabIndex,
            }}
          />
        )
      case DESCRIPTION_TAB:
        return <DescriptionForm page={getCBSDescriptionPage(tabIndex)} />
      case FAQ_TAB:
        return <FAQ page={getCBSFaqPage(tabIndex)} />
    }
    return <></>
  }

  if (!selectedPlatform || !subject) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        {getQBTitle(true, tabIndex, true)}
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={TABS} value={activeTab} onChange={handleChangeTab} />

      {renderTab()}
    </section>
  )
}
